import { NetworkStatusBanner } from '@/hooks/use-network-status';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  pageVariants,
  tabVariants,
  transition,
  useNavigation,
} from '../../hooks/use-navigation';
import { useKeyboardStore } from '../../stores/keyboard';
import { Button } from '../ui/button';
import { ClockIcon } from '../ui/clock';
import { FileTextIcon } from '../ui/file-text';
import { HomeIcon } from '../ui/home';
import { SettingsIcon } from '../ui/settings';

interface MobileNavigationProps {
  className?: string;
}

const fullscreenRoutes = [
  /^\/reports\/[^/]+\/record$/,
  /^\/reports\/[^/]+\/edit$/,
  /^\/templates\/create$/,
  /^\/templates\/[^/]+$/,
];

export function MobileNavigation({ className }: MobileNavigationProps) {
  const { t } = useTranslation();
  const {
    location,
    outlet,
    isAnimating,
    setIsAnimating,
    isTabSwitching,
    isActive,
    handleNavigate,
    stopAllAnimations,
    homeIconRef,
    clockIconRef,
    fileTextIconRef,
    settingsIconRef,
    bottomNavRef,
  } = useNavigation();
  const { isOpen: isKeyboardOpen } = useKeyboardStore();

  const isFullscreen = fullscreenRoutes.some((pattern) =>
    pattern.test(location.pathname),
  );

  return (
    <div
      className={cn(
        'text-foreground flex h-full flex-col overflow-hidden',
        className,
      )}>
      <NetworkStatusBanner />
      <div className="relative grow">
        <AnimatePresence
          initial={false}
          onExitComplete={() => setIsAnimating(false)}>
          <motion.div
            key={location.pathname}
            variants={isTabSwitching ? tabVariants : pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={transition}
            className="absolute inset-0 flex min-h-0 grow flex-col md:pb-0">
            {outlet}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Mobile Navigation Bar */}
      {!isFullscreen && (
        <nav
          className={cn(
            'z-50 container mx-auto grid w-full grid-cols-4 items-center bg-white px-4 pt-4 transition-[opacity,border-color] duration-200 md:hidden',
            isKeyboardOpen && 'pointer-events-none opacity-0',
          )}
          ref={bottomNavRef}>
          {/* Home Tab */}
          <Button
            disabled={isAnimating}
            onClick={() => {
              stopAllAnimations('home');
              homeIconRef.current?.startAnimation();
              handleNavigate('/');
            }}
            variant="ghost"
            className={cn(
              'flex h-auto flex-col items-center gap-1 p-0 hover:bg-transparent disabled:opacity-100',
              isActive('/') ? 'text-voicefill-black' : 'text-gray-400/70',
            )}>
            <HomeIcon
              ref={homeIconRef}
              className={cn(
                'size-5',
                isActive('/') ? 'stroke-primary' : 'stroke-gray-400/70',
              )}
            />
            <span className="text-xs font-medium">{t('home:title')}</span>
          </Button>

          {/* Reports Tab */}
          <Button
            disabled={isAnimating}
            onClick={() => {
              stopAllAnimations('reports');
              clockIconRef.current?.startAnimation();
              handleNavigate('/reports');
            }}
            variant="ghost"
            className={cn(
              'flex h-auto flex-col items-center gap-1 p-0 hover:bg-transparent disabled:opacity-100',
              isActive('/reports')
                ? 'text-voicefill-black'
                : 'text-gray-400/70',
            )}>
            <ClockIcon
              ref={clockIconRef}
              className={cn(
                'size-5',
                isActive('/reports') ? 'stroke-primary' : 'stroke-gray-400/70',
              )}
            />
            <span className="text-xs font-medium">{t('report:title')}</span>
          </Button>

          {/* Templates Tab */}
          <Button
            disabled={isAnimating}
            onClick={() => {
              stopAllAnimations('templates');
              fileTextIconRef.current?.startAnimation();
              handleNavigate('/templates');
            }}
            variant="ghost"
            className={cn(
              'flex h-auto flex-col items-center gap-1 p-0 hover:bg-transparent disabled:opacity-100',
              isActive('/templates')
                ? 'text-voicefill-black'
                : 'text-gray-400/70',
            )}>
            <FileTextIcon
              ref={fileTextIconRef}
              className={cn(
                'size-5',
                isActive('/templates')
                  ? 'stroke-primary'
                  : 'stroke-gray-400/70',
              )}
            />
            <span className="text-xs font-medium">{t('templates:title')}</span>
          </Button>

          {/* Settings Tab */}
          <Button
            disabled={isAnimating}
            onClick={() => {
              stopAllAnimations('settings');
              settingsIconRef.current?.startAnimation();
              handleNavigate('/settings');
            }}
            variant="ghost"
            className={cn(
              'flex h-auto flex-col items-center gap-1 p-0 hover:bg-transparent disabled:opacity-100',
              isActive('/settings')
                ? 'text-voicefill-black'
                : 'text-gray-400/70',
            )}>
            <SettingsIcon
              ref={settingsIconRef}
              className={cn(
                'size-5',
                isActive('/settings') ? 'stroke-primary' : 'stroke-gray-400/70',
              )}
            />
            <span className="text-xs font-medium">{t('settings:title')}</span>
          </Button>
        </nav>
      )}
    </div>
  );
}
